import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Role } from '@/lib/permissions';

/**
 * Bills of materials. The schema (migration 09) models a BOM as
 *
 *   boms (one per assembly item) → bom_versions → bom_lines (components)
 *
 * and a sub-assembly is nothing more than a component that has a BOM of
 * its own, so a multi-level tree falls out of following
 * `bom_lines.component_item_id` back into `boms.item_id`.
 *
 * The org's whole BOM set is small (tens of assemblies, hundreds of
 * lines), so the builder loads it in one round-trip (`useBomGraph`) and
 * does every explosion, roll-up, and where-used walk client-side.
 */

export interface BomItemRef {
  id: string;
  sku: string;
  name: string;
  kind: string;
  additional_kinds: string[];
  base_uom: string;
  status: string;
  parent_sku: string | null;
  category: string | null;
  model_filters: string[];
  color: string | null;
  region: string | null;
}

export interface BomLine {
  id: string;
  bom_version_id: string;
  component_item_id: string;
  quantity: number;
  uom: string;
  scrap_pct: number;
  notes: string | null;
  position: number;
  item: BomItemRef | null;
}

export interface BomVersionSummary {
  id: string;
  version: number;
  is_active: boolean;
  effective_from: string;
}

export interface BomRecord {
  id: string;
  item_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  item: BomItemRef | null;
  /** The version the builder edits: the active one, else the newest. */
  version: BomVersionSummary | null;
  versionCount: number;
  /** Lines on `version`, in display order. */
  lines: BomLine[];
}

export interface BomGraph {
  boms: BomRecord[];
  byItemId: Map<string, BomRecord>;
  /** component item id → ids of the assemblies whose BOM lists it. */
  usedIn: Map<string, Set<string>>;
  /** Cost per UoM Produced from `item_current_cost`: latest PO landed cost, else the supplier price. */
  costByItemId: Map<string, number>;
}

const ITEM_REF_COLUMNS =
  'id, sku, name, kind, additional_kinds, base_uom, status, parent_sku, category, model_filters, color, region';

export const BOM_GRAPH_KEY = ['bom-graph'] as const;

/** BOM writes are admin / super_admin only (RLS `boms_write_admin` et al.). */
export function canEditBoms(role: Role | null | undefined): boolean {
  return role === 'super_admin' || role === 'admin';
}

export type BomCategory = 'finished_good' | 'sub_assembly';

export type BomType = BomCategory | 'component';

export const BOM_TYPES: ReadonlyArray<{ value: BomType; label: string }> = [
  { value: 'finished_good', label: 'Finished goods' },
  { value: 'sub_assembly', label: 'Sub-assemblies' },
  { value: 'component', label: 'Components' },
];

export const BOM_TYPE_LABEL: Record<BomType, string> = {
  finished_good: 'Finished good',
  sub_assembly: 'Sub-assembly',
  component: 'Component',
};

const looksLikeSubAssembly = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .startsWith('subass');

/**
 * Item-kind slugs that mean "sub-assembly". Migration 91 removed the
 * system Sub-assembly kind, so orgs mint their own (e.g. "Sub Assy",
 * slug `sub_assy`); match on slug or label rather than one fixed slug.
 */
export function subAssemblyKindSlugs(
  kinds: ReadonlyArray<{ slug: string; label: string }> | undefined,
): Set<string> {
  const out = new Set<string>();
  for (const k of kinds ?? []) {
    if (looksLikeSubAssembly(k.slug) || looksLikeSubAssembly(k.label)) out.add(k.slug);
  }
  return out;
}

/**
 * Which BOM buckets an item belongs to. Like the catalog's per-type
 * pages, buckets overlap: an item checked as several types is listed
 * under each of them.
 *
 *   * Finished good — has the Finished good type.
 *   * Component     — has the Components type (`raw_material`).
 *   * Sub-assembly  — has a sub-assembly type, or has a BOM of its own
 *                     and is either built into another BOM or isn't a
 *                     finished good.
 */
export function bomTypesForItem(
  item: Pick<BomItemRef, 'id' | 'kind' | 'additional_kinds'>,
  graph: BomGraph | undefined,
  subAssemblyKinds: ReadonlySet<string>,
): Set<BomType> {
  const kinds = [item.kind, ...(item.additional_kinds ?? [])];
  const isFinishedGood = kinds.includes('finished_good');
  const hasBom = !!graph?.byItemId.get(item.id);
  const feedsAnotherBom = (graph?.usedIn.get(item.id)?.size ?? 0) > 0;

  const types = new Set<BomType>();
  if (isFinishedGood) types.add('finished_good');
  if (
    kinds.some((k) => subAssemblyKinds.has(k)) ||
    (hasBom && (feedsAnotherBom || !isFinishedGood))
  ) {
    types.add('sub_assembly');
  }
  if (kinds.includes('raw_material')) types.add('component');
  return types;
}

type RawLine = Omit<BomLine, 'item'> & { items: BomItemRef | null };
type RawBom = Omit<BomRecord, 'item' | 'version' | 'versionCount' | 'lines'> & {
  items: BomItemRef | null;
  bom_versions: Array<BomVersionSummary & { bom_lines: RawLine[] }>;
};

function sortLines(lines: BomLine[]): BomLine[] {
  return [...lines].sort(
    (a, b) =>
      a.position - b.position ||
      (a.item?.sku ?? '').localeCompare(b.item?.sku ?? '') ||
      a.id.localeCompare(b.id),
  );
}

export function useBomGraph() {
  return useQuery({
    queryKey: BOM_GRAPH_KEY,
    queryFn: async (): Promise<BomGraph> => {
      const [{ data, error }, costs] = await Promise.all([
        supabase
          .from('boms' as never)
          .select(
            `id, item_id, name, description, is_active, created_at, updated_at, items:item_id(${ITEM_REF_COLUMNS}), bom_versions(id, version, is_active, effective_from, bom_lines(id, bom_version_id, component_item_id, quantity, uom, scrap_pct, notes, position, items:component_item_id(${ITEM_REF_COLUMNS})))`,
          ),
        supabase.from('item_current_cost' as never).select('item_id, unit_cost_cents'),
      ]);
      if (error) throw error;
      if (costs.error) throw costs.error;
      const costByItemId = new Map<string, number>();
      for (const row of (costs.data ?? []) as unknown as Array<{ item_id: string; unit_cost_cents: number | string }>) {
        costByItemId.set(row.item_id, Number(row.unit_cost_cents));
      }

      const boms: BomRecord[] = ((data ?? []) as unknown as RawBom[]).map((raw) => {
        const versions = raw.bom_versions ?? [];
        const version =
          versions.find((v) => v.is_active) ??
          [...versions].sort((a, b) => b.version - a.version)[0] ??
          null;
        const lines = sortLines(
          (version?.bom_lines ?? []).map(({ items, ...line }) => ({
            ...line,
            // Quantities are in the component's UoM Produced (migration 161).
            uom: items?.base_uom || line.uom,
            quantity: Number(line.quantity),
            scrap_pct: Number(line.scrap_pct ?? 0),
            position: line.position ?? 0,
            item: items,
          })),
        );
        const { items, bom_versions: _versions, ...rest } = raw;
        return {
          ...rest,
          item: items,
          version: version
            ? {
                id: version.id,
                version: version.version,
                is_active: version.is_active,
                effective_from: version.effective_from,
              }
            : null,
          versionCount: versions.length,
          lines,
        };
      });

      const byItemId = new Map<string, BomRecord>();
      const usedIn = new Map<string, Set<string>>();
      for (const bom of boms) {
        byItemId.set(bom.item_id, bom);
        for (const line of bom.lines) {
          const parents = usedIn.get(line.component_item_id) ?? new Set<string>();
          parents.add(bom.item_id);
          usedIn.set(line.component_item_id, parents);
        }
      }
      return { boms, byItemId, usedIn, costByItemId };
    },
  });
}

/** True when `itemId` has a BOM with at least one line on its working version. */
export function hasBomLines(graph: BomGraph | undefined, itemId: string): boolean {
  return (graph?.byItemId.get(itemId)?.lines.length ?? 0) > 0;
}

/**
 * Every assembly that (at any depth) consumes `itemId`. Adding one of
 * these, or the item itself, as a component of `itemId` would close a
 * loop — the picker disables them and the DB trigger (migration 114)
 * rejects them.
 */
export function ancestorIds(graph: BomGraph | undefined, itemId: string): Set<string> {
  const out = new Set<string>();
  if (!graph) return out;
  const stack = [itemId];
  while (stack.length) {
    const id = stack.pop()!;
    for (const parent of graph.usedIn.get(id) ?? []) {
      if (!out.has(parent)) {
        out.add(parent);
        stack.push(parent);
      }
    }
  }
  return out;
}

const lineFactor = (line: Pick<BomLine, 'quantity' | 'scrap_pct'>) =>
  line.quantity * (1 + (line.scrap_pct || 0) / 100);

export interface RolledCost {
  /** Cost per unit in cents; sub-assemblies roll up from their lines. */
  cents: number;
  /** Leaf parts (at any depth) with no PO or supplier cost on file. */
  missing: number;
}

/**
 * Per-unit cost of an item: the sum of its BOM lines (quantity × scrap
 * allowance × component cost, recursively) when it has a BOM, else its
 * current cost per UoM Produced (latest PO landed cost, else the supplier
 * price). Memoised per graph.
 */
export function makeCostResolver(graph: BomGraph | undefined) {
  const memo = new Map<string, RolledCost>();
  const resolve = (itemId: string, stack: Set<string>): RolledCost => {
    const cached = memo.get(itemId);
    if (cached) return cached;
    const bom = graph?.byItemId.get(itemId);
    let result: RolledCost;
    if (bom && bom.lines.length > 0 && !stack.has(itemId)) {
      stack.add(itemId);
      result = { cents: 0, missing: 0 };
      for (const line of bom.lines) {
        const child = resolve(line.component_item_id, stack);
        result.cents += lineFactor(line) * child.cents;
        result.missing += child.missing;
      }
      stack.delete(itemId);
    } else {
      const current = graph?.costByItemId.get(itemId);
      result = current == null ? { cents: 0, missing: 1 } : { cents: current, missing: 0 };
    }
    memo.set(itemId, result);
    return result;
  };
  return (itemId: string) => resolve(itemId, new Set());
}

export interface BomTreeNode {
  /** Unique per occurrence in the tree (a part can appear under several sub-assemblies). */
  key: string;
  /** Outline number, e.g. "2.3.1". */
  path: string;
  depth: number;
  line: BomLine;
  /** The assembly whose BOM owns `line`. */
  parentItemId: string;
  /** Position of `line` among its siblings, for move up / down. */
  siblingIndex: number;
  siblingCount: number;
  /** Quantity needed for the whole build (scrap included). */
  extQty: number;
  unitCost: RolledCost;
  extCostCents: number;
  isSubAssembly: boolean;
  /** The component already appears above this node — not expanded. */
  isCycle: boolean;
  children: BomTreeNode[];
}

export function buildBomTree(
  graph: BomGraph | undefined,
  rootItemId: string,
  buildQty = 1,
): BomTreeNode[] {
  if (!graph) return [];
  const cost = makeCostResolver(graph);
  const walk = (
    assemblyId: string,
    multiplier: number,
    prefix: string,
    depth: number,
    lineage: Set<string>,
  ): BomTreeNode[] => {
    const lines = graph.byItemId.get(assemblyId)?.lines ?? [];
    return lines.map((line, index) => {
      const path = prefix ? `${prefix}.${index + 1}` : String(index + 1);
      const extQty = multiplier * lineFactor(line);
      const unitCost = cost(line.component_item_id);
      const isCycle = lineage.has(line.component_item_id);
      const isSubAssembly = hasBomLines(graph, line.component_item_id);
      let children: BomTreeNode[] = [];
      if (isSubAssembly && !isCycle) {
        lineage.add(line.component_item_id);
        children = walk(line.component_item_id, extQty, path, depth + 1, lineage);
        lineage.delete(line.component_item_id);
      }
      return {
        key: `${prefix}/${line.id}`,
        path,
        depth,
        line,
        parentItemId: assemblyId,
        siblingIndex: index,
        siblingCount: lines.length,
        extQty,
        unitCost,
        extCostCents: extQty * unitCost.cents,
        isSubAssembly,
        isCycle,
        children,
      };
    });
  };
  return walk(rootItemId, buildQty, '', 0, new Set([rootItemId]));
}

export function flattenTree(nodes: BomTreeNode[]): BomTreeNode[] {
  const out: BomTreeNode[] = [];
  const visit = (list: BomTreeNode[]) => {
    for (const node of list) {
      out.push(node);
      visit(node.children);
    }
  };
  visit(nodes);
  return out;
}

export interface FlatPart {
  item: BomItemRef | null;
  itemId: string;
  uom: string;
  totalQty: number;
  unitCostCents: number | null;
  extCostCents: number;
  /** Names of the assemblies (root or sub-assembly) the part is pulled into. */
  usedUnder: string[];
}

/** Purchasable leaf parts summed across every level — the pick list for a build. */
export function flatPartsList(
  graph: BomGraph | undefined,
  rootItemId: string,
  nodes: BomTreeNode[],
): FlatPart[] {
  const parts = new Map<string, FlatPart & { under: Set<string> }>();
  for (const node of flattenTree(nodes)) {
    if (node.isSubAssembly && !node.isCycle) continue;
    const key = `${node.line.component_item_id}|${node.line.uom}`;
    const existing = parts.get(key);
    const parentName =
      node.parentItemId === rootItemId
        ? 'Top level'
        : (graph?.byItemId.get(node.parentItemId)?.item?.name ?? 'Sub-assembly');
    if (existing) {
      existing.totalQty += node.extQty;
      existing.extCostCents += node.extCostCents;
      existing.under.add(parentName);
    } else {
      parts.set(key, {
        item: node.line.item,
        itemId: node.line.component_item_id,
        uom: node.line.uom,
        totalQty: node.extQty,
        unitCostCents: node.unitCost.missing ? null : node.unitCost.cents,
        extCostCents: node.extCostCents,
        usedUnder: [],
        under: new Set([parentName]),
      });
    }
  }
  return [...parts.values()]
    .map(({ under, ...p }) => ({ ...p, usedUnder: [...under] }))
    .sort((a, b) => (a.item?.sku ?? '').localeCompare(b.item?.sku ?? ''));
}

export interface BomTreeStats {
  directLines: number;
  subAssemblies: number;
  uniqueParts: number;
  depth: number;
  unitCost: RolledCost;
}

export function bomTreeStats(
  graph: BomGraph | undefined,
  rootItemId: string,
  nodes: BomTreeNode[],
): BomTreeStats {
  const all = flattenTree(nodes);
  const subs = new Set<string>();
  const leaves = new Set<string>();
  let depth = 0;
  for (const n of all) {
    depth = Math.max(depth, n.depth + 1);
    if (n.isSubAssembly) subs.add(n.line.component_item_id);
    else leaves.add(n.line.component_item_id);
  }
  return {
    directLines: nodes.length,
    subAssemblies: subs.size,
    uniqueParts: leaves.size,
    depth,
    unitCost: makeCostResolver(graph)(rootItemId),
  };
}

// -----------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------

function useInvalidateBoms() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: BOM_GRAPH_KEY });
    qc.invalidateQueries({ queryKey: ['bom-for-item'] });
    qc.invalidateQueries({ queryKey: ['boms-containing-item'] });
  };
}

function friendlyBomError(err: unknown): Error {
  const e = err as { code?: string; message?: string };
  if (e?.code === '42501') return new Error('Only admins can edit bills of materials.');
  if (e?.message) return new Error(e.message);
  return err instanceof Error ? err : new Error('Could not update the BOM.');
}

/**
 * Find-or-create the BOM header and working version for an assembly.
 * Mirrors `useAddItemToBom`: the active version, else the newest, else
 * a fresh active v1.
 */
async function ensureWorkingVersion(
  organizationId: string,
  assemblyItemId: string,
  assemblyName: string,
): Promise<{ bomId: string; versionId: string }> {
  const { data: existing, error: findErr } = await supabase
    .from('boms' as never)
    .select('id, bom_versions(id, version, is_active)')
    .eq('item_id', assemblyItemId)
    .maybeSingle();
  if (findErr) throw findErr;

  let bomId: string;
  let versionId: string | null = null;
  if (existing) {
    const bom = existing as unknown as {
      id: string;
      bom_versions: Array<{ id: string; version: number; is_active: boolean }>;
    };
    bomId = bom.id;
    const versions = bom.bom_versions ?? [];
    versionId =
      versions.find((v) => v.is_active)?.id ??
      [...versions].sort((a, b) => b.version - a.version)[0]?.id ??
      null;
  } else {
    const { data: created, error } = await supabase
      .from('boms' as never)
      .insert({
        organization_id: organizationId,
        item_id: assemblyItemId,
        name: assemblyName.trim() || 'Bill of materials',
        is_active: true,
      } as never)
      .select('id')
      .single();
    if (error) throw error;
    bomId = (created as unknown as { id: string }).id;
  }

  if (!versionId) {
    const { data: createdVer, error } = await supabase
      .from('bom_versions' as never)
      .insert({ bom_id: bomId, version: 1, is_active: true } as never)
      .select('id')
      .single();
    if (error) throw error;
    versionId = (createdVer as unknown as { id: string }).id;
  }
  return { bomId, versionId };
}

/** Create an (empty) BOM for an item that doesn't have one yet. */
export function useCreateBom() {
  const { profile } = useAuth();
  const invalidate = useInvalidateBoms();
  return useMutation({
    mutationFn: async (input: { itemId: string; name: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      try {
        return await ensureWorkingVersion(profile.organization_id, input.itemId, input.name);
      } catch (err) {
        throw friendlyBomError(err);
      }
    },
    onSuccess: invalidate,
  });
}

export interface NewBomLine {
  componentItemId: string;
  quantity: number;
  uom: string;
  scrapPct?: number;
  notes?: string | null;
}

/**
 * Append one or more components to an assembly's BOM, creating the BOM
 * on the fly. Components already on the BOM are skipped (and reported)
 * rather than duplicated.
 */
export function useAddBomLines() {
  const { profile } = useAuth();
  const invalidate = useInvalidateBoms();
  return useMutation({
    mutationFn: async (input: {
      assemblyItemId: string;
      assemblyName: string;
      lines: NewBomLine[];
    }): Promise<{ added: number; skipped: number }> => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (input.lines.length === 0) return { added: 0, skipped: 0 };
      for (const l of input.lines) {
        if (l.componentItemId === input.assemblyItemId) {
          throw new Error('An item can’t be a component of itself.');
        }
        if (!Number.isFinite(l.quantity) || l.quantity <= 0) {
          throw new Error('Every quantity must be greater than zero.');
        }
      }
      try {
        const { versionId } = await ensureWorkingVersion(
          profile.organization_id,
          input.assemblyItemId,
          input.assemblyName,
        );
        const { data: existing, error: exErr } = await supabase
          .from('bom_lines' as never)
          .select('component_item_id, position')
          .eq('bom_version_id', versionId);
        if (exErr) throw exErr;
        const rows = (existing ?? []) as unknown as Array<{
          component_item_id: string;
          position: number;
        }>;
        const onBom = new Set(rows.map((r) => r.component_item_id));
        let next = rows.reduce((max, r) => Math.max(max, r.position ?? 0), 0);

        const seen = new Set<string>();
        const payload = input.lines
          .filter((l) => {
            if (onBom.has(l.componentItemId) || seen.has(l.componentItemId)) return false;
            seen.add(l.componentItemId);
            return true;
          })
          .map((l) => ({
            bom_version_id: versionId,
            component_item_id: l.componentItemId,
            quantity: l.quantity,
            uom: l.uom.trim() || 'each',
            scrap_pct: l.scrapPct ?? 0,
            notes: l.notes?.trim() ? l.notes.trim() : null,
            position: ++next,
          }));

        if (payload.length > 0) {
          const { error } = await supabase.from('bom_lines' as never).insert(payload as never);
          if (error) throw error;
        }
        return { added: payload.length, skipped: input.lines.length - payload.length };
      } catch (err) {
        throw friendlyBomError(err);
      }
    },
    onSuccess: invalidate,
  });
}

export function useUpdateBomLine() {
  const invalidate = useInvalidateBoms();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      /** Swap the part on the line (e.g. White base → Black base), keeping qty. */
      componentItemId?: string;
      quantity?: number;
      uom?: string;
      scrapPct?: number;
      notes?: string | null;
    }) => {
      const patch: Record<string, unknown> = {};
      if (input.componentItemId !== undefined) patch.component_item_id = input.componentItemId;
      if (input.quantity !== undefined) {
        if (!Number.isFinite(input.quantity) || input.quantity <= 0) {
          throw new Error('Quantity must be greater than zero.');
        }
        patch.quantity = input.quantity;
      }
      if (input.uom !== undefined) patch.uom = input.uom.trim() || 'each';
      if (input.scrapPct !== undefined) {
        if (!Number.isFinite(input.scrapPct) || input.scrapPct < 0 || input.scrapPct >= 1000) {
          throw new Error('Scrap % must be between 0 and 999.');
        }
        patch.scrap_pct = input.scrapPct;
      }
      if (input.notes !== undefined) patch.notes = input.notes?.trim() ? input.notes.trim() : null;
      const { error } = await supabase
        .from('bom_lines' as never)
        .update(patch as never)
        .eq('id', input.id);
      if (error) throw friendlyBomError(error);
    },
    onSuccess: invalidate,
  });
}

export function useDeleteBomLine() {
  const invalidate = useInvalidateBoms();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bom_lines' as never)
        .delete()
        .eq('id', id);
      if (error) throw friendlyBomError(error);
    },
    onSuccess: invalidate,
  });
}

/**
 * Persist a new order for a version's lines. Takes the full ordered id
 * list so positions are rewritten densely (1..n) and stay unambiguous.
 */
export function useReorderBomLines() {
  const invalidate = useInvalidateBoms();
  return useMutation({
    mutationFn: async (orderedLineIds: string[]) => {
      const results = await Promise.all(
        orderedLineIds.map((id, i) =>
          supabase
            .from('bom_lines' as never)
            .update({ position: i + 1 } as never)
            .eq('id', id),
        ),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw friendlyBomError(failed.error);
    },
    onSuccess: invalidate,
  });
}

/** Delete an assembly's BOM header (versions and lines cascade). */
export function useDeleteBom() {
  const invalidate = useInvalidateBoms();
  return useMutation({
    mutationFn: async (bomId: string) => {
      const { error } = await supabase
        .from('boms' as never)
        .delete()
        .eq('id', bomId);
      if (error) throw friendlyBomError(error);
    },
    onSuccess: invalidate,
  });
}

export const formatQty = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 4 });

/** Product family label for a finished good, e.g. "Renew Manual" from "Renew Manual - White, …". */
export function familyLabel(item: Pick<BomItemRef, 'name' | 'parent_sku'>): string {
  const head = item.name.split(' - ')[0]?.trim();
  return head || item.parent_sku || item.name;
}

/**
 * Cost per UoM Produced for any item: the BOM roll-up when it has lines,
 * else its current cost (latest PO landed cost, else the supplier price).
 * Null when nothing prices it.
 */
export function useItemUnitCostOf() {
  const { data: graph } = useBomGraph();
  return useMemo(() => {
    const resolve = makeCostResolver(graph);
    return (itemId: string): number | null => {
      if (!graph) return null;
      if (!hasBomLines(graph, itemId)) return graph.costByItemId.get(itemId) ?? null;
      return resolve(itemId).cents;
    };
  }, [graph]);
}
